/**
 * Set de ícones do Hub — SVG de traço, `viewBox` 24×24, `currentColor`.
 *
 * Substitui os glifos de texto que o projeto usava antes (`Σ`, `⏳`, `◑`,
 * `✕`, `▾`, emoji): eles herdavam a fonte do sistema, não tinham peso
 * consistente entre si e renderizavam diferente em cada OS — o que dava o
 * aspecto "genérico" que o Rodrigo apontou. Aqui o traço é uniforme
 * (`strokeWidth` 1.9 nos ícones de KPI, 2.2 nos mini-botões, que são
 * menores e precisam de mais peso pra não sumir).
 *
 * Convenção: todo ícone aceita `size` e herda `color` do pai via
 * `currentColor` — nunca hardcodear cor aqui, quem decide é o componente
 * que usa (chip de KPI, botão, badge de status).
 */

interface IconProps { size?: number }

function Svg({ size = 20, sw = 1.9, children }: IconProps & { sw?: number; children: React.ReactNode }) {
  return (
    <svg
      width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round"
      style={{ display: 'block', flex: 'none' }}
    >{children}</svg>
  );
}

// ---- KPIs -----------------------------------------------------------------

/** Somatório — Perda Bruta. */
export const IconSum = (p: IconProps) => (
  <Svg {...p}><path d="M18 5H7l7 7-7 7h11" /></Svg>
);

/** Escudo com check — Perda Confirmada (receita validada). */
export const IconShieldCheck = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 3l7 3v5.5c0 4.5-2.9 7.9-7 9.5-4.1-1.6-7-5-7-9.5V6z" />
    <path d="M9 12l2.2 2.2L15.5 10" />
  </Svg>
);

/** Ampulheta — Fila Pendente. */
export const IconHourglass = (p: IconProps) => (
  <Svg {...p}>
    <path d="M7 3h10M7 21h10" />
    <path d="M8 3v3.2c0 1.3.6 2.5 1.7 3.3L12 11l2.3-1.5C15.4 8.7 16 7.5 16 6.2V3" />
    <path d="M8 21v-3.2c0-1.3.6-2.5 1.7-3.3L12 13l2.3 1.5c1.1.8 1.7 2 1.7 3.3V21" />
  </Svg>
);

/** Meia-lua de progresso — Cobertura da auditoria. */
export const IconCoverage = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 3.5a8.5 8.5 0 010 17z" fill="currentColor" stroke="none" />
  </Svg>
);

/** Triângulo de alerta — Taxa de exceção / inadimplência. */
export const IconAlert = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 4.2L21 19.5H3z" />
    <path d="M12 10v4" /><circle cx="12" cy="16.8" r=".6" fill="currentColor" stroke="none" />
  </Svg>
);

/** Cronômetro — Tempo médio de auditoria. */
export const IconTimer = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="13.5" r="7.5" />
    <path d="M12 9.5v4l2.5 1.8M9.5 2.5h5" />
  </Svg>
);

/** Grupo de pessoas — Clientes ativos. */
export const IconUsers = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="9.5" cy="8.5" r="3.3" />
    <path d="M3.5 19.2c.7-3.3 3.2-5.1 6-5.1s5.3 1.8 6 5.1" />
    <path d="M16.5 6.1a3.2 3.2 0 010 5.9M18.2 14.6c1.6.7 2.7 2.2 3.1 4.2" />
  </Svg>
);

// ---- Status de fatura / adimplência ---------------------------------------

export const IconCheck = (p: IconProps) => (
  <Svg sw={2.4} {...p}><path d="M5 12.5l4.5 4.5L19 7" /></Svg>
);

/** Relógio — pendente (esperando, não é erro). */
export const IconClock = (p: IconProps) => (
  <Svg {...p}><circle cx="12" cy="12" r="8.5" /><path d="M12 7.2V12l3.2 2.2" /></Svg>
);

/** Círculo tracejado — sem dado no período (nem bom nem ruim). */
export const IconCircleDashed = (p: IconProps) => (
  <Svg {...p}><circle cx="12" cy="12" r="8.5" strokeDasharray="3 3.2" /></Svg>
);

// ---- Eventos de perda ------------------------------------------------------

/** X — churn (perda total). */
export const IconX = (p: IconProps) => (
  <Svg sw={2.4} {...p}><path d="M6.5 6.5l11 11M17.5 6.5l-11 11" /></Svg>
);

/** Seta diagonal pra baixo — contraction (perda parcial). */
export const IconTrendDown = (p: IconProps) => (
  <Svg sw={2.2} {...p}><path d="M6 7.5l11 9M17 10.8v5.7h-5.7" /></Svg>
);

/** Seta diagonal pra cima — receita recuperada (espelho vertical de `IconTrendDown`). */
export const IconTrendUp = (p: IconProps) => (
  <Svg sw={2.2} {...p}><path d="M6 16.5l11-9M17 13.2V7.5h-5.7" /></Svg>
);

// ---- Ações (mini-botões) ---------------------------------------------------

export const IconPlus = (p: IconProps) => (
  <Svg sw={2.3} {...p}><path d="M12 6v12M6 12h12" /></Svg>
);

export const IconMinus = (p: IconProps) => (
  <Svg sw={2.3} {...p}><path d="M6 12h12" /></Svg>
);

export const IconChevronDown = (p: IconProps) => (
  <Svg sw={2.4} {...p}><path d="M6 9.5l6 6 6-6" /></Svg>
);

export const IconArrowRight = (p: IconProps) => (
  <Svg sw={2.3} {...p}><path d="M4.5 12h14M13 6.5l6 5.5-6 5.5" /></Svg>
);

/** Seta curva de volta — reverter auditoria. */
export const IconUndo = (p: IconProps) => (
  <Svg sw={2.2} {...p}>
    <path d="M4.5 9.5h9.8a5.2 5.2 0 010 10.4H8" />
    <path d="M8 5l-3.5 4.5L8 14" />
  </Svg>
);

/** Link externo — abrir no Stripe. */
export const IconExternal = (p: IconProps) => (
  <Svg sw={2} {...p}>
    <path d="M14 4.5h5.5V10" /><path d="M19.5 4.5L11 13" />
    <path d="M16.5 14v4.5a2 2 0 01-2 2h-9a2 2 0 01-2-2v-9a2 2 0 012-2H10" />
  </Svg>
);

/** Telefone — contadores de contato. */
export const IconPhone = (p: IconProps) => (
  <Svg sw={2} {...p}>
    <path d="M5.2 3.5h3l1.5 3.8-2 1.4a11.5 11.5 0 005.6 5.6l1.4-2 3.8 1.5v3a1.7 1.7 0 01-1.9 1.7C9.4 17.9 6.1 14.6 3.5 5.4A1.7 1.7 0 015.2 3.5z" />
  </Svg>
);

export const IconSearch = (p: IconProps) => (
  <Svg sw={2.1} {...p}><circle cx="11" cy="11" r="6.5" /><path d="M16 16l4.5 4.5" /></Svg>
);

export const IconChevronLeft = (p: IconProps) => (
  <Svg sw={2.4} {...p}><path d="M14.5 6l-6 6 6 6" /></Svg>
);

export const IconChevronRight = (p: IconProps) => (
  <Svg sw={2.4} {...p}><path d="M9.5 6l6 6-6 6" /></Svg>
);

/** Setas pra fora — expandir gráfico em Scale Modal. */
export const IconExpand = (p: IconProps) => (
  <Svg sw={2.1} {...p}>
    <path d="M14.5 4.5H19.5V9.5M9.5 19.5H4.5V14.5" />
    <path d="M19.5 4.5L13.8 10.2M4.5 19.5l5.7-5.7" />
  </Svg>
);

/** Duas folhas — copiar valor. */
export const IconCopy = (p: IconProps) => (
  <Svg sw={2} {...p}>
    <rect x="8.5" y="8.5" width="11" height="11" rx="2.4" />
    <path d="M15.5 8.5v-2a2 2 0 00-2-2h-7a2 2 0 00-2 2v7a2 2 0 002 2h2" />
  </Svg>
);

/** Bandeira — zona de falso positivo (churn a reconferir). */
export const IconFlag = (p: IconProps) => (
  <Svg sw={2} {...p}>
    <path d="M6 3v18" />
    <path d="M6 4.5c2-1.4 4-1.4 6 0s4 1.4 6 0v9c-2 1.4-4 1.4-6 0s-4-1.4-6 0z" />
  </Svg>
);

/** Elos de corrente — eventos correlacionados do mesmo cliente já auditados. */
export const IconLink = (p: IconProps) => (
  <Svg sw={2} {...p}>
    <path d="M9.5 14.5 14.5 9.5" />
    <path d="M11 6.5l1.5-1.5a3.2 3.2 0 0 1 4.5 4.5L15.5 11" />
    <path d="M13 17.5l-1.5 1.5a3.2 3.2 0 0 1-4.5-4.5L8.5 13" />
  </Svg>
);

/** Moldura com montanha/sol — evidência do tipo imagem (disclaimer de tipo na lista). */
export const IconImage = (p: IconProps) => (
  <Svg sw={1.9} {...p}>
    <rect x="3" y="4.5" width="18" height="15" rx="2.5" />
    <circle cx="8.3" cy="9.3" r="1.6" />
    <path d="M3 16.5l5.5-5.5 4 4 2.5-2.5L21 17" />
  </Svg>
);

/** Envelope — seção de emails do Contact Modal. */
export const IconMail = (p: IconProps) => (
  <Svg sw={1.9} {...p}>
    <rect x="3" y="5.5" width="18" height="13" rx="2.5" />
    <path d="M4 7l8 6 8-6" />
  </Svg>
);

/** Sol — modo claro (toggle de tema, Configurações pessoais). */
export const IconSun = (p: IconProps) => (
  <Svg sw={1.9} {...p}>
    <circle cx="12" cy="12" r="4.3" />
    <path d="M12 2.5v2.4M12 19.1v2.4M4.6 4.6l1.7 1.7M17.7 17.7l1.7 1.7M2.5 12h2.4M19.1 12h2.4M4.6 19.4l1.7-1.7M17.7 6.3l1.7-1.7" />
  </Svg>
);

/** Lua — modo escuro (toggle de tema, Configurações pessoais). */
export const IconMoon = (p: IconProps) => (
  <Svg sw={1.9} {...p}>
    <path d="M20 14.1A8.4 8.4 0 0 1 9.9 4a8.4 8.4 0 1 0 10.1 10.1z" />
  </Svg>
);

/** Setas duplas — migração técnica (troca de plano/assinatura), badge no card de evento. */
export const IconSwap = (p: IconProps) => (
  <Svg sw={2.1} {...p}>
    <path d="M6 8h13M15 4l4 4-4 4" />
    <path d="M18 16H5M9 12l-4 4 4 4" />
  </Svg>
);

/** Seta subindo pra uma nuvem — exportar auditoria pro pipeline externo (Nekt). */
export const IconUpload = (p: IconProps) => (
  <Svg sw={2} {...p}>
    <path d="M12 16V6M8 10l4-4 4 4" />
    <path d="M5 18h14" />
  </Svg>
);

/** Engrenagem — configurações pessoais (avatar, preferências). */
export const IconSettings = (p: IconProps) => (
  <Svg sw={1.9} {...p}>
    <circle cx="12" cy="12" r="3.2" />
    <path d="M19.4 13.6a1.7 1.7 0 0 0 .34 1.87l.06.06a2.06 2.06 0 1 1-2.92 2.92l-.06-.06a1.7 1.7 0 0 0-1.87-.34 1.7 1.7 0 0 0-1.03 1.56v.17a2.06 2.06 0 1 1-4.12 0v-.09a1.7 1.7 0 0 0-1.11-1.56 1.7 1.7 0 0 0-1.87.34l-.06.06a2.06 2.06 0 1 1-2.92-2.92l.06-.06a1.7 1.7 0 0 0 .34-1.87 1.7 1.7 0 0 0-1.56-1.03h-.17a2.06 2.06 0 1 1 0-4.12h.09a1.7 1.7 0 0 0 1.56-1.11 1.7 1.7 0 0 0-.34-1.87l-.06-.06a2.06 2.06 0 1 1 2.92-2.92l.06.06a1.7 1.7 0 0 0 1.87.34h.08a1.7 1.7 0 0 0 1.03-1.56v-.17a2.06 2.06 0 1 1 4.12 0v.09a1.7 1.7 0 0 0 1.03 1.56h.08a1.7 1.7 0 0 0 1.87-.34l.06-.06a2.06 2.06 0 1 1 2.92 2.92l-.06.06a1.7 1.7 0 0 0-.34 1.87v.08a1.7 1.7 0 0 0 1.56 1.03h.17a2.06 2.06 0 1 1 0 4.12h-.09a1.7 1.7 0 0 0-1.56 1.03z" />
  </Svg>
);
